import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { useApi, imgUrl, euro, errMsg } from "@/lib/api";
import { useUser } from "@/context/UserContext";
import { ListingCard } from "@/components/ListingCard";
import { SellerBadge } from "@/components/SellerBadge";
import { ProfileForm } from "@/components/ProfileSetup";
import { DeliveryLabel } from "@/components/Delivery";

const STATUS = {
  pending: ["In attesa", "bg-amber-50 text-amber-700"], accepted: ["Accettata", "bg-emerald-50 text-emerald-700"],
  rejected: ["Rifiutata", "bg-rose-50 text-rose-700"], active: ["Attivo", "bg-emerald-50 text-emerald-700"],
  sold: ["Venduto", "bg-slate-900 text-white"], hidden: ["Sospeso", "bg-rose-50 text-rose-700"],
};
const Status = ({ s }) => <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS[s]?.[1]}`}>{STATUS[s]?.[0]}</span>;
const Empty = ({ text }) => <p className="text-slate-500 py-16 text-center border border-dashed rounded-2xl">{text}</p>;

const Row = ({ image, title, sub, right, testid, link }) => (
  <div data-testid={testid} className="flex items-center gap-4 p-4 bg-white border border-slate-200 rounded-2xl">
    <Link to={link} className="shrink-0"><img src={imgUrl(image)} alt="" className="w-20 h-14 rounded-lg object-cover" /></Link>
    <div className="min-w-0 flex-1">
      <Link to={link} className="font-heading font-semibold truncate block hover:text-blue-600">{title}</Link>
      <div className="text-sm text-slate-500">{sub}</div>
    </div>
    <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">{right}</div>
  </div>
);

export default function Dashboard() {
  const api = useApi();
  const { me, favIds } = useUser();
  const [params, setParams] = useSearchParams();
  const isAdmin = me?.role === "admin";
  const ADMIN_TABS = ["inserzioni", "ricevute", "vendite"];
  const requested = params.get("tab");
  const tab = isAdmin ? requested || "inserzioni" : requested && !ADMIN_TABS.includes(requested) ? requested : "inviate";
  const [d, setD] = useState({ listings: [], received: [], sent: [], purchases: [], sales: [], favorites: [] });

  const load = useCallback(async () => {
    try {
      const [listings, received, sent, purchases, sales, fav] = await Promise.all([
        api("get", "/my/listings"), api("get", "/my/offers/received"), api("get", "/my/offers/sent"),
        api("get", "/my/purchases"), api("get", "/my/sales"), api("get", "/favorites")]);
      setD({ listings, received, sent, purchases, sales, favorites: fav.listings });
    } catch (e) { toast.error(errMsg(e)); }
  }, [api]);
  useEffect(() => { load(); }, [load, favIds.length]);

  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); } };
  const decide = (o, action) => act(() => api("post", `/offers/${o.id}/decision`, { action }), action === "accept" ? "Offerta accettata, veicolo venduto" : "Offerta rifiutata");
  const remove = (l) => window.confirm("Eliminare l'annuncio?") && act(() => api("delete", `/listings/${l.id}`), "Annuncio eliminato");
  const pending = d.received.filter((o) => o.status === "pending").length;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Area personale</h1>
        {me?.account_type && <SellerBadge type={me.account_type} />}
      </div>
      <p className="text-slate-500 mt-1" data-testid="dashboard-user-name">{me?.company_name || me?.display_name} · {me?.email}</p>
      <Tabs value={tab} onValueChange={(v) => setParams({ tab: v })} className="mt-8">
        <TabsList className="flex-wrap h-auto">
          {isAdmin && <TabsTrigger value="inserzioni" data-testid="dashboard-tab-my-listings">Inserzioni</TabsTrigger>}
          {isAdmin && <TabsTrigger value="ricevute" data-testid="dashboard-tab-received-offers">Offerte ricevute{pending ? ` (${pending})` : ""}</TabsTrigger>}
          <TabsTrigger value="inviate" data-testid="dashboard-tab-sent-offers">Offerte inviate</TabsTrigger>
          <TabsTrigger value="acquisti" data-testid="dashboard-tab-purchases">Acquisti</TabsTrigger>
          {isAdmin && <TabsTrigger value="vendite" data-testid="dashboard-tab-sales">Vendite</TabsTrigger>}
          <TabsTrigger value="preferiti" data-testid="dashboard-tab-favorites">Preferiti</TabsTrigger>
          <TabsTrigger value="profilo" data-testid="dashboard-tab-profile">Profilo</TabsTrigger>
        </TabsList>

        <TabsContent value="inserzioni" className="space-y-3 mt-6">
          {d.listings.length === 0 && <Empty text="Non hai ancora pubblicato annunci." />}
          {d.listings.map((l) => (
            <Row key={l.id} testid={`my-listing-${l.id}`} link={`/veicolo/${l.id}`} image={l.images?.[0]} title={`${l.brand} ${l.model}`}
              sub={`${euro(l.price)} · ${l.accept_offers ? "Accetta offerte" : "Prezzo fisso"}`}
              right={<>
                <Status s={l.status} />
                {l.status !== "sold" && <Button asChild size="sm" variant="outline" data-testid={`my-listing-edit-${l.id}`}><Link to={`/modifica/${l.id}`}>Modifica</Link></Button>}
                <Button size="sm" variant="ghost" className="text-rose-600" data-testid={`my-listing-delete-${l.id}`} onClick={() => remove(l)}>Elimina</Button>
              </>} />
          ))}
        </TabsContent>

        <TabsContent value="ricevute" className="space-y-3 mt-6">
          {d.received.length === 0 && <Empty text="Nessuna offerta ricevuta." />}
          {d.received.map((o) => (
            <Row key={o.id} testid={`received-offer-${o.id}`} link={`/veicolo/${o.listing_id}`} image={o.image} title={o.title}
              sub={<><span className="font-mono text-amber-700 font-medium">{euro(o.amount)}</span> su {euro(o.asking_price)} · da {o.buyer_name}<DeliveryLabel item={o} />{o.message && <span className="block italic">"{o.message}"</span>}</>}
              right={o.status === "pending" ? <>
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" data-testid={`offer-accept-${o.id}`} onClick={() => decide(o, "accept")}>Accetta</Button>
                <Button size="sm" variant="outline" data-testid={`offer-reject-${o.id}`} onClick={() => decide(o, "reject")}>Rifiuta</Button>
              </> : <Status s={o.status} />} />
          ))}
        </TabsContent>

        <TabsContent value="inviate" className="space-y-3 mt-6">
          {d.sent.length === 0 && <Empty text="Non hai inviato offerte." />}
          {d.sent.map((o) => (
            <Row key={o.id} testid={`sent-offer-${o.id}`} link={`/veicolo/${o.listing_id}`} image={o.image} title={o.title}
              sub={<>La tua offerta <span className="font-mono text-amber-700">{euro(o.amount)}</span> su {euro(o.asking_price)}<DeliveryLabel item={o} /></>} right={<Status s={o.status} />} />
          ))}
        </TabsContent>

        <TabsContent value="acquisti" className="space-y-3 mt-6">
          {d.purchases.length === 0 && <Empty text="Nessun acquisto effettuato." />}
          {d.purchases.map((p) => (
            <Row key={p.id} testid={`purchase-${p.id}`} link={`/veicolo/${p.listing_id}`} image={p.image} title={p.title}
              sub={<>{euro(p.total ?? p.price)} · {p.mode === "offerta" ? "Offerta accettata" : "Compra subito"} · Venditore: {p.seller_name}<DeliveryLabel item={p} /></>}
              right={<span className="text-xs text-slate-500">{new Date(p.created_at).toLocaleDateString("it-IT")}</span>} />
          ))}
        </TabsContent>

        <TabsContent value="vendite" className="space-y-3 mt-6">
          {d.sales.length === 0 && <Empty text="Nessuna vendita conclusa." />}
          {d.sales.map((p) => (
            <Row key={p.id} testid={`sale-${p.id}`} link={`/veicolo/${p.listing_id}`} image={p.image} title={p.title}
              sub={<>{euro(p.total ?? p.price)} · {p.mode === "offerta" ? "Offerta accettata" : "Compra subito"} · Acquirente: {p.buyer_name || "—"}<DeliveryLabel item={p} /></>}
              right={<span className="text-xs text-slate-500">{new Date(p.created_at).toLocaleDateString("it-IT")}</span>} />
          ))}
        </TabsContent>

        <TabsContent value="preferiti" className="mt-6">
          {d.favorites.length === 0 ? <Empty text="Nessun veicolo salvato." /> : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">{d.favorites.map((l, i) => <ListingCard key={l.id} listing={l} index={i} />)}</div>
          )}
        </TabsContent>

        <TabsContent value="profilo" className="mt-6 max-w-lg"><ProfileForm /></TabsContent>
      </Tabs>
    </div>
  );
}
